/** Smart Sharpen v1 — luminance-only Unsharp Mask + halo suppression.
 *
 *  Two differences from `filter-unsharp-mask`:
 *
 *    1. Operates on the **Y channel of BT.601 YCbCr** rather than RGB.
 *       Sharpening luminance only means saturated edges (e.g. red text
 *       on cyan) don't pick up the chromatic fringing that per-channel
 *       sharpening creates. Cb/Cr are passed through untouched.
 *
 *    2. **Halo suppression** clamps the per-pixel sharpening
 *       contribution to a user-configurable limit, preventing the
 *       bright white halos that aggressive USM produces around
 *       high-contrast edges.
 *
 *  Full deconvolution Smart Sharpen (Photoshop's "Remove: Gaussian /
 *  Lens / Motion") is intentionally deferred — it's a research-grade
 *  implementation and its tuning surface is too large for v1.
 *
 *  Cross-filter dependency: this module imports `buildGaussianKernel1D`
 *  from `./gaussian-blur` so the kernel math is provably identical to
 *  the standalone Gaussian filter. If gaussian-blur's kernel signature
 *  changes, this file must update in lockstep. The non-cumulative
 *  contract test exercises both filters so kernel-shape regressions
 *  surface there. */

import {
  BT601_CB_B,
  BT601_CB_G,
  BT601_CB_R,
  BT601_CB_TO_B,
  BT601_CB_TO_G,
  BT601_CR_B,
  BT601_CR_G,
  BT601_CR_R,
  BT601_CR_TO_G,
  BT601_CR_TO_R,
  BT601_LUMA_B,
  BT601_LUMA_G,
  BT601_LUMA_R,
} from '../color-constants';
import { buildGaussianKernel1D } from './gaussian-blur';

export interface SmartSharpenValue {
  /** Strength (0..500%). 0 = identity. */
  amount: number;
  /** Blur radius (px) for the unsharp computation. */
  radius: number;
  /** Per-pixel luminance threshold (0..255) — sharpening below this
   *  level of detail is skipped. */
  threshold: number;
  /** Halo suppression (0..100). Clamps the difference contribution
   *  per pixel; smaller values produce gentler edges, larger values
   *  allow more aggressive sharpening at the cost of haloing. */
  haloSuppression: number;
}

export const identitySmartSharpenValue = (): SmartSharpenValue => ({
  amount: 0,
  // 3px default — a 1px radius only catches single-pixel detail, which is
  // imperceptible on high-resolution sources, so "full strength" amount
  // looked very light. (amount: 0 keeps this an identity on open.)
  radius: 3,
  threshold: 0,
  haloSuppression: 50,
});

/** Separable 1D Gaussian on a scalar Float32 buffer. Mirrors the math
 *  in `gaussian-blur.ts` but operates on a single-channel float buffer
 *  so we don't have to pack/unpack Y through an RGBA ImageData just
 *  to run the existing RGB convolver. Reuses the kernel construction
 *  from the same module so the math is provably identical. */
function gaussianBlurScalar(src: Float32Array, w: number, h: number, radius: number): Float32Array {
  const r = Math.max(0, Math.floor(radius));
  if (r === 0) return new Float32Array(src);
  const kernel = buildGaussianKernel1D(r);
  const temp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  const maxX = w - 1;
  const maxY = h - 1;
  for (let y = 0; y < h; y++) {
    const rowOff = y * w;
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let k = -r; k <= r; k++) {
        const sx = Math.min(maxX, Math.max(0, x + k));
        acc += src[rowOff + sx] * kernel[k + r];
      }
      temp[rowOff + x] = acc;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let k = -r; k <= r; k++) {
        const sy = Math.min(maxY, Math.max(0, y + k));
        acc += temp[sy * w + x] * kernel[k + r];
      }
      out[y * w + x] = acc;
    }
  }
  return out;
}

export function applySmartSharpenPixels(source: ImageData, value: SmartSharpenValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, value.amount);
  const radius = Math.max(0, value.radius);
  if (amount === 0 || radius === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const threshold = Math.max(0, Math.min(255, value.threshold));
  // haloSuppression=0 → no clamp (allow full diff), 100 → tight clamp
  // (±25.5 = 10% of full range). The mapping caps the clamp at 10%
  // of 255 at maximum tightness so the slider feels like a "subtle
  // edges" control rather than a kill-switch.
  const halo = Math.max(0, Math.min(100, value.haloSuppression));
  const haloLimit = 255 * (1 - halo / 100) + 25.5 * (halo / 100);
  const k = amount / 100;
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  const n = w * h;

  // RGB → YCbCr (BT.601). Y stays in 0..255; Cb/Cr in 0..255 (128-centered).
  const Y = new Float32Array(n);
  const Cb = new Float32Array(n);
  const Cr = new Float32Array(n);
  for (let i = 0, p = 0; i < src.length; i += 4, p++) {
    const r = src[i];
    const g = src[i + 1];
    const b = src[i + 2];
    Y[p] = BT601_LUMA_R * r + BT601_LUMA_G * g + BT601_LUMA_B * b;
    Cb[p] = BT601_CB_R * r + BT601_CB_G * g + BT601_CB_B * b + 128;
    Cr[p] = BT601_CR_R * r + BT601_CR_G * g + BT601_CR_B * b + 128;
  }

  // Blur Y, compute diff with threshold + halo clamp, write back.
  const Yblur = gaussianBlurScalar(Y, w, h, radius);
  const Ynew = new Float32Array(n);
  for (let p = 0; p < n; p++) {
    const diff = Y[p] - Yblur[p];
    if (Math.abs(diff) < threshold) {
      Ynew[p] = Y[p];
      continue;
    }
    const clipped = Math.max(-haloLimit, Math.min(haloLimit, diff));
    Ynew[p] = Y[p] + k * clipped;
  }

  // YCbCr → RGB (BT.601). Clamping happens at Uint8ClampedArray write.
  for (let i = 0, p = 0; i < out.length; i += 4, p++) {
    const y = Ynew[p];
    const cb = Cb[p] - 128;
    const cr = Cr[p] - 128;
    out[i] = y + BT601_CR_TO_R * cr;
    out[i + 1] = y + BT601_CB_TO_G * cb + BT601_CR_TO_G * cr;
    out[i + 2] = y + BT601_CB_TO_B * cb;
    out[i + 3] = src[i + 3];
  }

  return new ImageData(out, w, h);
}

export function applySmartSharpenToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: SmartSharpenValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applySmartSharpenPixels(source, value), 0, 0);
}
